import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatError } from "../utils/api";
import { Eye, EyeOff, Leaf } from "lucide-react";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handle = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        if (!form.name.trim()) return setError("Name is required");
        await register(form.email, form.password, form.name);
      }
      navigate("/");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-cream">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-sage-800">
        <img
          src="https://images.unsplash.com/photo-1557804506-669a67965ba0?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
          alt="background"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-2">
            <Leaf className="w-8 h-8 text-sage-300" />
            <span className="font-heading text-2xl font-semibold tracking-tight text-white">TheKaizenCabinet</span>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-semibold text-white leading-tight mb-4">
              Workforce optimisation,<br />driven by data.
            </h1>
            <p className="text-sage-200 text-lg font-body leading-relaxed max-w-md">
              Model current-state staffing, simulate lean improvement scenarios, and generate actionable diagnostics for operational excellence.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[["8 Modules", "End-to-end wizard"], ["Real-time", "FTE calculations"], ["9 Domains", "Industry templates"]].map(([t, d]) => (
                <div key={t} className="bg-white/10 rounded-md p-3 border border-white/20">
                  <div className="font-heading text-lg font-semibold text-white">{t}</div>
                  <div className="text-sage-300 text-xs mt-1">{d}</div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-sage-400 text-xs">TheKaizenCabinet — Your Lean Toolbox</p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Leaf className="w-6 h-6 text-sage-500" />
            <span className="font-heading text-xl font-semibold text-sage-800">TheKaizenCabinet</span>
          </div>

          <h2 className="font-heading text-3xl font-semibold text-sage-900 mb-1">
            {mode === "login" ? "Welcome back" : "Create account"}
          </h2>
          <p className="text-sage-500 text-sm mb-8 font-body">
            {mode === "login" ? "Sign in to your consultant portal" : "Start your TheKaizenCabinet workspace"}
          </p>

          <form onSubmit={handle} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Full Name</label>
                <input
                  data-testid="auth-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name"
                  className="w-full border border-sage-200 rounded-md px-3 py-2.5 text-sm focus:border-sage-500 focus:ring-1 focus:ring-sage-500 outline-none transition-colors bg-white"
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                className="w-full border border-sage-200 rounded-md px-3 py-2.5 text-sm focus:border-sage-500 focus:ring-1 focus:ring-sage-500 outline-none transition-colors bg-white"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Password</label>
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full border border-sage-200 rounded-md px-3 py-2.5 text-sm focus:border-sage-500 focus:ring-1 focus:ring-sage-500 outline-none transition-colors bg-white pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sage-400 hover:text-sage-600"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="auth-error" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-sage-600 text-white rounded-md py-2.5 font-semibold text-sm hover:bg-sage-700 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:transform-none"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="auth-mode-toggle"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-sage-600 hover:text-sage-800 text-sm font-medium transition-colors"
            >
              {mode === "login" ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
